import { User } from '@redux/prop-types';
import { AccessLevel } from '@utils/enums/access-levels';

type DateRange = [Date | string | null, Date | string | null];

function asDateRange(value: unknown): DateRange | undefined {
  if (Array.isArray(value) && value.length >= 2) {
    return value as DateRange;
  }
  return undefined;
}

export type UserAccess = {
  isSCTEC: boolean;
  isNationalUser: boolean;
  isNationalGroupUser: boolean | undefined;
  isPROrPIReproved: boolean | 0 | null | undefined;
  hasCompletedMundoTech: boolean;
  needsPlacementTest: boolean;
  shouldShowNumberAttemptsOver: boolean | 0 | null | undefined;
  shouldShowWaitingList: boolean | null | undefined;
  shouldShowSecondChance: boolean | 0 | undefined;
  shouldRedirectToPlacementTest: boolean;
  shouldShowValidityStartEndClassGroup: boolean;
  shouldShowDropuotUserMessage: boolean;
  verifyClassGroupStarted: boolean | null;
  isModularOrLimitedAccess: boolean;
  confirmedRegistration: boolean;
};

export const useUserAccess = (user: User): UserAccess => {
  const isSCTEC = () => {
    return user?.source === 'devstart-sctec';
  };

  const isNationalUser = () => {
    return !!user?.group?.includes('nacional-');
  };

  const isNationalGroupUser = () => {
    return !user?.source && user?.group?.includes('nacional-');
  };

  const isPROrPIReproved = () => {
    return (
      ['nacional-pi-I-02/2025', 'nacional-pr-I-02/2025'].includes(
        user?.source ?? ''
      ) &&
      user?.attemptedPlacementTestCounter &&
      user.attemptedPlacementTestCounter <= 2 &&
      user?.studentEvaluation &&
      user.studentEvaluation.studentGrade < 7
    );
  };

  const hasCompletedMundoTech = () => {
    return user?.completedChallenges?.some(
      (challenge: { id: string }) => challenge.id === '66fbe66d4368241d29757851'
    );
  };

  const needsPlacementTest = () => {
    return (
      user?.needsPlacementTest ||
      (user.registrationQuiz === 1 &&
        !user.isFundamentosDaProgramacaoFrontEndCert &&
        hasCompletedMundoTech() &&
        (user.attemptedPlacementTestCounter ?? 0) < 2)
    );
  };

  const shouldShowNumberAttemptsOver = () => {
    return (
      isNationalUser() &&
      user?.registrationQuiz === 1 &&
      user?.attemptedPlacementTestCounter &&
      user.attemptedPlacementTestCounter >= 2 &&
      user?.studentEvaluation &&
      user?.studentEvaluation?.studentGrade < 7
    );
  };

  const shouldShowWaitingList = () => {
    return (
      isNationalGroupUser() &&
      !user?.needsPlacementTest &&
      user?.studentEvaluation &&
      user.studentEvaluation.studentGrade >= 7 &&
      !user.source &&
      Object.entries(user.classGroupControl || {}).length === 0
    );
  };

  const shouldShowSecondChance = () => {
    return (
      isNationalGroupUser() &&
      user?.registrationQuiz === 1 &&
      user?.completedChallenges?.length === 0 &&
      user?.attemptedPlacementTestCounter &&
      user.attemptedPlacementTestCounter < 2 &&
      !user?.needsPlacementTest
    );
  };

  const verifyClassGroupStarted = () => {
    const { validityType } = user?.classGroupInfo || {};
    const validityDateRange = asDateRange(
      user?.classGroupInfo?.validityDateRange
    );

    switch (validityType) {
      case 'dateRange': {
        if (!Array.isArray(validityDateRange)) return false;

        const startDate = validityDateRange[0];
        const endDate = validityDateRange[1];
        const now = new Date();
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        const hasStarted = start && start <= now;
        const hasNotExpired = !end || now <= end;

        return hasStarted && hasNotExpired;
      }

      case 'durationInMonths':
        return !user?.isBlockedByExpiration;

      default:
        return false;
    }
  };

  const shouldShowValidityStartEndClassGroup = () => {
    if (user.registrationQuiz === 1 && !user.source) {
      return false;
    }

    if (user?.classGroupInfo?.validityType !== 'dateRange') {
      return false;
    }

    if (confirmedRegistration()) {
      const dateRange = asDateRange(user?.classGroupInfo?.validityDateRange);
      if (Array.isArray(dateRange)) {
        const startDate = dateRange[0];
        const currentDate = new Date();
        const endDate = dateRange[1];

        if (startDate && new Date(startDate) > currentDate) {
          return true;
        }

        if (endDate && new Date(endDate) < currentDate) {
          return true;
        }

        return false;
      }
    } else {
      return true;
    }

    return true;
  };

  const shouldShowDropuotUserMessage = () => {
    function isDateOlderThanFiveDays(date: Date) {
      try {
        const targetDate = new Date(date);

        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid date provided');
        }

        const currentDate = new Date();

        targetDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        const differenceMilliseconds: number =
          currentDate.getTime() - targetDate.getTime();
        const differenceDays = differenceMilliseconds / (1000 * 60 * 60 * 24);

        return differenceDays > 5;
      } catch (error) {
        console.error('Error processing date:', error);
        return false;
      }
    }

    const wasMarkedByAdmin =
      user.classGroupControl?.markedAsDropoutByAdmin === true;
    const hasExpiredLinkedDate = user.classGroupControl?.linkedToTheClassDate
      ? isDateOlderThanFiveDays(
          new Date(user.classGroupControl.linkedToTheClassDate)
        )
      : false;

    return (
      isNationalUser() &&
      user.classGroupControl?.acceptedInvite === false &&
      (wasMarkedByAdmin || hasExpiredLinkedDate)
    );
  };

  const shouldRedirectToPlacementTest = () => {
    return (
      isNationalUser() && needsPlacementTest() && !user.isBlockedByExpiration
    );
  };

  const isModularOrLimitedAccess = () => {
    return (
      user.access === AccessLevel.MODULAR || user.access === AccessLevel.LIMITED
    );
  };

  const confirmedRegistration = () => {
    return user.classGroupControl?.acceptedInvite === true;
  };

  return {
    isSCTEC: isSCTEC(),
    isNationalUser: isNationalUser(),
    isNationalGroupUser: isNationalGroupUser(),
    isPROrPIReproved: isPROrPIReproved(),
    hasCompletedMundoTech: hasCompletedMundoTech(),
    needsPlacementTest: needsPlacementTest(),
    shouldShowNumberAttemptsOver: shouldShowNumberAttemptsOver(),
    shouldShowWaitingList: shouldShowWaitingList(),
    shouldShowSecondChance: shouldShowSecondChance(),
    shouldRedirectToPlacementTest: shouldRedirectToPlacementTest(),
    shouldShowValidityStartEndClassGroup:
      shouldShowValidityStartEndClassGroup(),
    shouldShowDropuotUserMessage: shouldShowDropuotUserMessage(),
    verifyClassGroupStarted: verifyClassGroupStarted(),
    isModularOrLimitedAccess: isModularOrLimitedAccess(),
    confirmedRegistration: confirmedRegistration()
  };
};
